import type { Updater } from '@tanstack/vue-table'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

import type { Ref } from 'vue'
import { getSavedLocale, i18n } from '@/plugins/i18n'

const API_SERVER_BASE = (
  (import.meta.env.VITE_API_URL as string) || 'http://localhost:5001/api'
).replace(/\/api\/?$/, '')

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export function extractApiErrorMessage(error: unknown, t: TranslateFn) {
  const fallback = t('toasts.network_error')

  if (!error || typeof error !== 'object') {
    return fallback
  }

  const maybeAxios = error as {
    response?: { data?: unknown; status?: number }
    message?: string
  }

  const data = maybeAxios.response?.data as { error?: unknown; message?: unknown } | undefined

  if (data?.error && typeof data.error === 'object') {
    const errorObj = data.error as Record<string, string[]>
    const firstKey = Object.keys(errorObj)[0]
    if (firstKey && errorObj[firstKey]?.[0]) return errorObj[firstKey][0]
  }

  const apiMessage =
    (typeof data?.error === 'string' && data.error.trim()) ||
    (typeof data?.message === 'string' && data.message.trim()) ||
    ''

  if (apiMessage) return apiMessage

  if (maybeAxios.response?.status) {
    return t('toasts.request_failed', { status: maybeAxios.response.status })
  }

  if (typeof maybeAxios.message === 'string' && maybeAxios.message.trim()) {
    return maybeAxios.message
  }

  return fallback
}

const LATIN_DIGITS_LOCALES: Record<string, string> = {
  ar: 'ar-IQ-u-nu-latn',
  en: 'en-US'
}

const IQD_CURRENCY_LABELS: Record<string, string> = {
  ar: 'د.ع',
  en: 'IQD'
}

function getCurrentLocale() {
  return i18n?.global?.locale?.value ?? getSavedLocale()
}

function getLocaleWithLatinDigits() {
  const current = getCurrentLocale()
  return LATIN_DIGITS_LOCALES[current] ?? 'en-US'
}

function getIqdCurrencyLabel() {
  const current = getCurrentLocale()
  return IQD_CURRENCY_LABELS[current] ?? 'IQD'
}

function toValidDate(value?: string | number | Date | null) {
  if (value === null || value === undefined) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(getLocaleWithLatinDigits(), options).format(value)
}

export function formatOptionalNumber(value?: number | null, options?: Intl.NumberFormatOptions) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-'
  return formatNumber(value, options)
}

export function formatDate(
  value?: string | number | Date | null,
  options?: Intl.DateTimeFormatOptions
) {
  const date = toValidDate(value)
  return date ? date.toLocaleDateString(getLocaleWithLatinDigits(), options) : '-'
}

export function formatTime(
  value?: string | number | Date | null,
  options?: Intl.DateTimeFormatOptions
) {
  const date = toValidDate(value)
  return date ? date.toLocaleTimeString(getLocaleWithLatinDigits(), options) : '-'
}

export function formatActivityTime(value?: string | number | Date | null) {
  const date = toValidDate(value)
  if (!date)
    return '-'

  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 86400000)

  if (date >= todayStart || date >= yesterdayStart) {
    return new Intl.DateTimeFormat(getLocaleWithLatinDigits(), {
      hour: 'numeric',
      minute: 'numeric',
    }).format(date)
  }

  return new Intl.DateTimeFormat(getLocaleWithLatinDigits(), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date)
}

export function formatCurrency(
  value?: number | null,
  currency?: string | null,
  options?: Intl.NumberFormatOptions
) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-'
  const trimmed = currency?.trim()
  if (!trimmed) return formatNumber(value, options)

  return `${formatNumber(value, options)} ${trimmed}`
}

export function getImageUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined
  return `${API_SERVER_BASE}${path}`
}
